import type { AskedMaintenanceAction } from '@volee/api-client'

/** What a maintenance action's confirmation asks, and what the box will do. */
interface MaintenanceConfirmation {
  readonly title: string
  readonly body: string
}

// The interface strings — the SOURCE: `UiMessages` derives from this object, so `it/ui.ts`
// cannot drift. No `as const`, or a translation would have to repeat the English text.
export const EN_UI = {
  app: {
    name: 'Volée Back Office',
    navigationLabel: 'Main navigation',
    openMenu: 'Open the navigation menu',
    closeMenu: 'Close the navigation menu',
    collapseMenu: 'Narrow the menu to its icons',
    expandMenu: 'Show the menu labels',
  },
  // The words a pager uses, identical in every section: four groups said them four times.
  pagination: {
    position: 'Page {page} of {pageCount}',
    previous: 'Previous page',
    next: 'Next page',
  },
  navigation: {
    home: 'Home',
    club: 'Clubs',
    pitch: 'Pitches',
    kit: 'Kits',
    player: 'Players',
    users: 'Users',
    register: 'Roles',
    leagues: 'Leagues',
    bulletinSources: 'Comunicati',
    matches: 'Fixtures',
    staff: 'Staff',
    settings: 'Settings',
    signIn: 'Sign in',
    signOut: 'Sign out',
    breadcrumb: 'Breadcrumb',
  },
  bulletinSource: {
    title: 'Comunicati',
    description: 'The pages Volée fetches the delegations’ comunicati from.',
    list: 'Sources of comunicati',
    loading: 'Loading the sources',
    listError: 'The sources could not be loaded.',
    empty: 'No sources',
    emptyDescription: 'Add a delegation’s comunicati page.',
    noMatch: 'No source matches.',
    statusLabel: 'Status',
    all: 'All',
    activeMany: 'On',
    inactiveMany: 'Off',
    searchLabel: 'Search by name or address',
    add: 'Add source',
    addTitle: 'Add a source',
    addDescription: 'It starts switched off: switch it on from the list.',
    editTitle: 'Edit the source',
    edit: 'Edit {name}',
    name: 'Name',
    nameHelp: 'How you recognise it: “Delegazione di Genova”.',
    indexUrl: 'Page address',
    indexUrlHelp: 'The page listing the comunicati, with https://',
    requiredField: 'Required field',
    create: 'Add',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    created: 'Source added, switched off',
    updated: 'Source updated',
    toggle: 'Switch {name} on',
    switchedOn: '{name} switched on',
    switchedOff: '{name} switched off',
    fixesTitle: 'To sort out',
    kindClub: 'Club not recognised',
    kindPitch: 'Pitch not recognised',
    kindRead: 'Reading stopped by the check',
    kindCategory: 'Category not handled',
    kindOther: 'Not written',
    fixCreateClub: 'Create the club',
    fixClubExists: 'It is a club we have',
    fixOurPitch: 'It is one of our pitches',
    fixNotOurs: 'Not ours',
    fixReread: 'Read again',
    fixIgnore: 'Ignore',
    fixWhichClub: 'Which club?',
    fixWhichPitch: 'Which pitch?',
    fixSearching: 'Searching',
    fixNoMatch: 'No match',
    fixClubCreated: 'Club «{name}» created',
    fixClubMatched: '«{name}» is now {club}',
    fixPitchMatched: '«{name}» is now the pitch {pitch}',
    fixRereadDone: 'The model will read that part again',
    fixIgnored: 'It will not show again',
    fixIgnoreOnce: 'Just this time',
    fixIgnoreAlways: 'Always',
    fixIgnoredOnce: 'It will not show in this check',
    ignoredTitle: 'Ignored',
    ignoredOn: 'Ignored on {when}',
    restore: 'Restore',
    restored: 'Restored: back among the things to sort out',
    fixDone: 'Sorted out: applies from the next check',
    fixesReady: '{count} sorted out: press «Check now» when you are done',
    checkNow: 'Check now',
    checkNowLabel: 'Check {name} now',
    checkStarted: 'Check started on {name}',
    checkRunning: 'Check running…',
    neverChecked: 'Never checked',
    checkDone: 'Checked on {when} · {inserted} new matches, {updated} updated',
    checkSkipped: '{count} skipped',
    checkRefused: '{count} gironi refused',
    checkFailed: 'Check failed on {when}: {error}',
  },
  userMenu: {
    // Carries the email: a control named only "Account" would say what it opens and not
    // who is signed in, which is the whole question this header failed to answer.
    open: 'Account and session: {email}',
  },
  dashboard: {
    title: 'Volée Back Office',
    // Addressed to the person who just signed in: the page's job is to say where they are,
    // who they are, and what is waiting on them.
    signedInAs: 'Signed in as {email}',
    // Every count is a list request for its `total`: the API has no summary endpoint.
    countsLoading: 'Loading the counts',
    countUnavailable: 'Unavailable',
    registerTitle: 'Register',
    clubsTotal: 'Clubs',
    pitchesTotal: 'Pitches',
    playersTotal: 'Players',
    requestsTitle: 'Sign-ups to review',
    requestsList: 'Sign-ups waiting for a decision',
    requestsLoading: 'Loading the sign-ups waiting',
    requestsError: 'The sign-ups waiting could not be loaded.',
    requestsEmpty: 'No sign-up is waiting',
    requestsEmptyDescription: 'A request appears here as soon as a club submits it.',
    openQueue: 'Open the queue',
    gatewaysTitle: 'Kits to check',
    gatewaysList: 'Kits that want a look',
    gatewaysLoading: 'Loading the fleet',
    gatewaysError: 'The fleet could not be loaded.',
    gatewaysEmpty: 'Every kit is answering',
    gatewaysEmptyDescription:
      'Every kit in service spoke within the hour and has nothing to check.',
    openFleet: 'Open the kits',
  },

  signIn: {
    title: 'Sign in to the back office',
    description: 'Reserved for Volée staff. Sign in with your Volée credentials.',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    forgotPassword: 'I forgot my password',
    // Why the user is on this page, keyed by the `reason` search param: an expired session
    // and a first visit would otherwise render the same page.
    reason: {
      'session-expired': 'Your session has expired. Sign in again to continue.',
      required: 'That page is reserved for signed-in staff.',
    },
  },
  newPassword: {
    title: 'Choose your password',
    description: 'The temporary password has to be replaced before you can continue.',
    submit: 'Save and continue',
    saved: 'Password set.',
  },
  // The second factor: the sign-in step, and the card that switches it on. TOTP only, and
  // optional — nobody is forced onto it. Why: docs/infra.md.
  mfa: {
    title: 'Enter the code from your app',
    description: 'Open your authenticator app and type the code it shows for Volée.',
    code: 'Code',
    submit: 'Sign in',
    cardTitle: 'Two-step verification',
    cardDescription: 'A code from an app on your phone, asked on top of your password.',
    statusOn: 'Two-step verification is on.',
    statusOff: 'Two-step verification is off.',
    lostDeviceTitle: 'If you lose the phone',
    lostDevice:
      'Keep the app on a phone you keep. If you lose it you cannot sign in on your own: write to us and we will switch it off for you.',
    enable: 'Turn on two-step verification',
    disable: 'Turn off',
    setupTitle: 'Scan the code',
    setupDescription:
      'Scan this with your authenticator app, or type the key into it by hand. Then enter the code the app shows.',
    secret: 'Key',
    copySecret: 'Copy the key',
    secretCopied: 'Key copied',
    unreadable: 'Cannot tell right now whether two-step verification is on. Try again later.',
    confirm: 'Confirm and turn on',
    cancel: 'Cancel',
    enabled: 'Two-step verification is on.',
    disabled: 'Two-step verification is off.',
  },
  passwordReset: {
    title: 'Reset your password',
    // Says nothing about whether the account exists: the page must not answer that question.
    description: 'Enter your email. If an account exists, a code arrives by email.',
    email: 'Email',
    request: 'Send the code',
    codeTitle: 'Enter the code',
    codeDescription: 'Check your inbox and enter the code, then choose a new password.',
    code: 'Code',
    confirm: 'Save the new password',
    done: 'Password updated.',
    signInYourself: 'Sign in with the new password.',
    backToSignIn: 'Back to sign-in',
  },
  password: {
    current: 'Current password',
    new: 'New password',
    confirm: 'Confirm the new password',
    // Mirrors the pool's policy through @volee/config: a hint, never a validator.
    policyHint:
      'At least {minLength} characters, with an uppercase letter, a lowercase letter and a digit.',
    mismatch: 'The two passwords do not match.',
    required: 'Required field.',
    changeTitle: 'Change password',
    changeDescription: 'You stay signed in here. Other devices keep their session.',
    changeSubmit: 'Change password',
    changed: 'Password changed.',
    // A global sign-out that failed leaves other devices alive: silence would be a lie.
    signOutPartial: 'You are signed out here, but other devices may still be signed in.',
  },
  settings: {
    title: 'Settings',
    description: 'Your account.',
    devBypassHint:
      'This session has no Cognito identity, so there is nothing to sign out of and no password to change.',
  },
  // The caller's own staff profile, the first card of /settings.
  profile: {
    cardTitle: 'Profile',
    description: 'How the rest of the staff sees you.',
    email: 'Email',
    emailHelp: 'Your sign-in address: it is not changed here.',
    lastName: 'Surname',
    firstName: 'First name',
    phone: 'Phone',
    photo: 'Photo',
    photoDescription: 'The picture on your staff profile.',
    photoAlt: 'Your photo',
    uploadPhoto: 'Upload photo',
    replacePhoto: 'Replace photo',
    removePhoto: 'Remove photo',
    photoSaved: 'Photo saved.',
    photoRemoved: 'Photo removed.',
    photoTooLarge: 'The file exceeds {maxMb} MB.',
    save: 'Save profile',
    saved: 'Profile saved.',
    loading: 'Loading your profile…',
    loadError: 'Your profile could not be loaded.',
  },
  session: {
    role: 'Role: {role}',
    noRole: 'No back-office role',
    expired: 'Session expired. Sign in again.',
    // Local development against the dev bypass: there is no Cognito session at all, so
    // anything offering to end one would be lying. Said once, read by every surface.
    devBypass: 'Dev bypass',
    devBypassHint: 'Local session without Cognito: signing out has no effect.',
  },
  // The club↔pitch link, which belongs to neither end: both cards render these.
  link: {
    primary: 'Usual pitch',
    makePrimaryForClub: 'Make it their usual pitch',
    makePrimaryPitch: 'Make it the usual one',
    detach: 'Detach',
    attached: 'Linked.',
    promoted: 'Usual pitch updated.',
    detached: 'Unlinked.',
  },
  club: {
    title: 'Clubs',
    description: 'The register of clubs. Protected area: staff only.',

    // The list
    list: 'Registered clubs',
    searchLabel: 'Search by legal name or short name',
    statusFilter: 'Filter by status',
    allStatuses: 'All statuses',
    sortLabel: 'Sort by',
    sortLegalNameAsc: 'Legal name (A–Z)',
    sortLegalNameDesc: 'Legal name (Z–A)',
    sortCreatedDesc: 'Newest first',
    sortCreatedAsc: 'Oldest first',
    loading: 'Loading clubs…',
    listError: 'The list of clubs could not be loaded.',
    // Two distinct empty states: "there are none" invites creating one, "your search found
    // nothing" must not — that leads to re-entering a club that exists and was not found.
    empty: 'No clubs registered yet.',
    emptyDescription: 'Add the first club to get started.',
    emptySearch: 'No club matches this search.',
    emptySearchDescription: 'Check the spelling, or clear the field to see them all.',
    clearSearch: 'Clear the search',
    paginationLabel: 'Pagination of the clubs',
    count: '{total} clubs registered',

    // The register, on create and on edit
    newClub: 'New club',
    newClubDescription:
      'Enter the register details. Pitches, devices and the contact person are linked later.',
    details: 'Register details',
    legalName: 'Legal name',
    legalNameHelp: 'The full name, as it appears on the deed of incorporation.',
    shortName: 'Short name',
    shortNameHelp: 'How it appears in lists and call-ups.',
    logo: 'Logo',
    logoAlt: 'Logo of {name}',
    uploadLogo: 'Upload logo',
    replaceLogo: 'Replace logo',
    changeLogo: 'Change logo',
    removeLogo: 'Remove logo',
    logoSaved: 'Logo saved.',
    logoRemoved: 'Logo removed.',
    logoTooLarge: 'The file exceeds {maxMb} MB.',
    logoUploadFailedAfterCreate:
      'The club was created, but the logo was not uploaded. Retry from its record.',
    address: 'Address',
    region: 'Region',
    province: 'Province',
    chooseRegion: 'Choose a region',
    chooseProvince: 'Choose a province',
    chooseRegionFirst: 'Choose the region first',
    noProvince: 'Not recorded',
    lndName: 'In the federation’s comunicati',
    lndNameHelp:
      'As the federation prints it, in full: A.N.P.I.SPORT E.CASASSA. The comunicati import recognises it on its own when the legal name matches; fill this in when the two names differ.',
    lndNameEmpty: 'Not bound',
    status: 'Status',
    statusMenu: 'Status: {status}. Change status',
    statusSaved: 'Status changed to {status}.',
    leaveActiveTitle: 'Change {name} to {status}?',
    leaveActiveBody:
      'Only active clubs receive enrolment requests from families: until it is active again, {name} will receive no new ones.',
    leaveActiveConfirm: 'Change status',
    requiredField: 'Required field.',
    create: 'Create club',
    creating: 'Creating…',
    edit: 'Edit',
    save: 'Save changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    created: 'Club created.',
    openCard: 'Open the record',
    saved: 'Changes saved.',
    clubNotFound: 'This club does not exist, or has been removed.',
    backToList: 'Back to the list',
    // The organigramma section of the card
    officials: 'Officials',

    // The teams section of the card
    teams: 'Teams',
    teamsDescription: 'The sides this club fields. Which league each plays comes later.',
    teamsLoading: 'Loading the teams…',
    teamsError: 'The teams of this club could not be loaded.',
    noTeams: 'This club fields no team yet.',
    noTeamsDescription: 'Add the first one below.',
    newTeam: 'Add a team',
    newTeamDescription:
      'A club fields one side per category: Allievi A and B are one team in two league groups.',
    ageGroup: 'Category',
    underAge: 'Age limit',
    gender: 'Gender',
    seniores: 'No limit (seniores)',
    bothYears: '1st and 2nd year',
    underAgeOption: 'Under {years}',
    category: 'Category',
    categoryOption: '{group} U{years} · {gender}',
    categoryOptionBothYears: '{group}, 1st and 2nd year · {gender}',
    categoryOptionSeniores: '{group} · {gender}',
    // Only from B on: the federation writes the first side as the bare club name.
    teamLetter: 'Squad {letter}',
    addTeam: 'Add',
    addingTeam: 'Adding…',
    teamCreated: 'Team added.',
    editTeam: 'Edit',
    saveTeam: 'Save',
    savingTeam: 'Saving…',
    cancelTeam: 'Cancel',
    teamSaved: 'Team saved.',
    deleteTeam: 'Delete',
    teamDeleted: 'Team deleted.',

    // A squad's staff, with the seat of whoever holds one. An incarico lives in a season, a team does
    // not, so this band follows the season picker while the teams above it do not.
    teamStaff: 'Squad staff',
    noTeamStaff: 'Nobody in the staff yet',
    teamSeatMark: 'Squad seat',
    teamStaffLoading: 'Loading the staff…',
    teamStaffError: 'The staff of this club could not be loaded.',
    addTeamStaff: 'Add a coach, without a seat',
    teamStaffPlaceholder: 'Pick one',
    noAssignableStaff: 'No coach left to add. Appoint one from Officials.',
    removeTeamStaff: 'Remove',
    removeTeamStaffOf: 'Remove {name}',
    teamStaffAdded: 'Added to the squad’s staff, without a seat.',
    teamStaffRemoved: 'Removed from the squad. They remain in the club’s staff.',

    // The campi section of the card
    pitches: 'Pitches',
    pitchesDescription: 'The campi this club plays on. One of them is the usual one.',
    pitchesLoading: 'Loading the pitches…',
    pitchesError: 'The pitches of this club could not be loaded.',
    noPitches: 'This club has no pitch yet.',
    noPitchesDescription: 'Attach one it already plays on, or create a new one below.',
    pitchCreated: 'Pitch created and attached.',
    attachPitch: 'Attach an existing pitch',
    attachPitchDescription: 'A municipal campo is shared: the same one can serve two clubs.',
    attachPitchLabel: 'Search the pitch to attach',
    attachPitchPlaceholder: 'Name or address',
    attachPitchSearching: 'Searching…',
    attachPitchNoMatch: 'No pitch matches this search.',
    newClubPitch: 'Create a pitch for this club',
    newClubPitchDescription: 'Created and attached in one request, so neither can happen alone.',
    createAndAttachPitch: 'Create and attach',
  },
  gateway: {
    inStock: 'In stock',
    kitName: 'Kit {number}',
    title: 'Kits',
    description:
      'What sits in a campo’s cabinet: the gateway, the router with its SIM, the cameras. Staff only.',

    // The kits
    list: 'Kits',
    searchLabel: 'Search by kit number, hardware ID or SIM',
    kind: 'Configuration',
    kinds: {
      raspberry_4g: 'Raspberry Pi and 4G router',
      nucbox_g10: 'GMKtec NucBox G10 and 4G router',
    },
    hostname: 'Hostname',
    noHostname: 'No hostname',
    assign: 'Install at a pitch',
    move: 'Move',
    placeTitle: 'Assign to a campo',
    moveTitle: 'Move to another campo',
    placeSave: 'Save the campo',
    placeSaving: 'Saving…',
    placed: 'Campo saved.',
    renameFrom:
      'In the Tailscale console (Machines → Edit machine name) rename the node from {old} to {new}.',
    renameFirst: 'In the Tailscale console (Machines → Edit machine name) name the node {new}.',
    renameHelp: 'The back office does not talk to Tailscale: the box is a reminder, not a check.',
    renameConfirm: 'I renamed the node on Tailscale',
    sshCommand: 'SSH access',
    copySsh: 'Copy the command',
    sshCopied: 'Command copied',
    consolePassword: 'Console password',
    routerPassword: 'Router password',
    cameraPassword: 'Camera password',
    showPasswords: 'Show the passwords',
    copyConsolePassword: 'Copy the console password',
    copyRouterPassword: 'Copy the router password',
    copyCameraPassword: 'Copy the camera password',
    passwordCopied: 'Password copied',
    passwordsLoading: 'Loading the passwords…',
    noConsolePassword: 'No console password: this kit was not prepared at the bench.',
    noRouterPassword: 'No router password: the bench did not prepare this kit’s router.',
    noCameraPassword: 'No camera password: the bench did not prepare this kit’s camera.',
    router: '4G router',
    routerModel: 'Router model',
    routerSerial: 'Router serial',
    routerFirmware: 'Router firmware',
    noRouterValue: 'Not given',
    health: 'Health',
    condition: 'Condition',
    identity: 'Identity',
    access: 'Access',
    moreActions: 'More',
    cameraDetails: 'Camera details',
    retiredDescription: 'A retired kit stays tied to its pitch and does not go back to stock.',
    upgradeAvailable: 'To update',
    upgradeAvailableDescription:
      'The gateway does not run the newest published version: it upgrades when it is not recording.',
    upgradeRequested: 'Upgrade requested',
    conditionReasons: 'Why it wants a check',
    lastSeenColumn: 'Last contact',
    sortLabel: 'Sort by',
    sortSerialAsc: 'Hardware ID (A–Z)',
    sortSerialDesc: 'Hardware ID (Z–A)',
    sortInstalledDesc: 'Most recently installed',
    sortInstalledAsc: 'Installed longest ago',
    sortLastSeenDesc: 'Last seen, most recent first',
    sortLastSeenAsc: 'Last seen, longest ago first',
    // A row's meta and value slots carry no column header of their own, unlike the detail
    // card's `CardListText label`: a bare date there names nothing.
    installedLine: 'Installed {date}',
    installedSince: 'since {date}',
    kitHeading: 'Kit',
    whereHeading: 'Where',
    boxHeading: 'Box',
    seenHeading: 'Last contact',
    stateHeading: 'State',
    lastSeenLine: 'Last seen {date}',
    // Until the heartbeat exists this is every box: silence is the honest answer, and
    // it is not an alarm — with the schedule on disk the gateway keeps recording.
    neverSeen: 'Never seen',
    retired: 'Retired',
    loading: 'Loading kits…',
    listError: 'The list of kits could not be loaded.',
    empty: 'No kit yet.',
    emptyDescription:
      'Switch on a gateway at the bench with its router connected: it appears above within a minute. No box? Check that the SIM has its PIN turned off and that the router has signal.',
    emptySearch: 'No kit matches this search.',
    emptySearchDescription: 'Check the spelling, or clear the field to see them all.',
    clearSearch: 'Clear the search',
    paginationLabel: 'Pagination of the kits',
    count: '{total} kits',
    viewsLabel: 'Which kits',
    viewAll: 'All',
    fleetTitle: 'Every kit',
    inStockTotal: 'In stock',
    installedTotal: 'Installed',
    attentionTotal: 'To check',
    retiredTotal: 'Retired',

    // The kits of one campo, and installing one from stock
    atPitch: 'Kits at this campo',
    pageDescription: 'The kit in this campo’s cabinet, and where one is installed from stock.',
    openList: 'Kits',
    installTitle: 'Install a kit from stock',
    installDescription:
      'Kits are prepared at the bench: here you choose which one goes into this cabinet.',
    kitPicker: 'Kit',
    kitPlaceholder: 'Search by number or hardware ID',
    kitSearching: 'Searching…',
    kitNoMatch: 'No kit in stock matches.',
    clearKit: 'Change kit',
    installSave: 'Install',
    // Adding a camera, and the scanner
    addCamera: 'Add the camera',
    cameraAdded: 'Camera added.',
    cameraName: 'Name',
    defaultCameraName: 'Camera',
    cameraMacHelp: 'From the router’s device list.',
    scan: {
      title: 'Frame the code',
      hint: 'Hold the label still and well lit.',
      cancel: 'Cancel',
      starting: 'Opening the camera…',
      denied:
        'Camera permission was denied: grant it in the browser settings, or type the code by hand.',
      unavailable: 'No camera available on this device: type the code by hand.',
    },
    scanCameraSerial: 'Read the serial number',
    scanCameraUid: 'Read the UID’s QR',
    pitchHelp: 'The campo whose cabinet the box sits in.',
    pitchPlaceholder: 'Search by name or address',
    pitchSearching: 'Searching…',
    pitchNoMatch: 'No campo matches.',
    clearPitch: 'Change campo',
    noneAtPitch: 'No box installed at this campo.',
    noneAtPitchDescription: 'Install one from stock below: kits are prepared at the bench.',
    hardwareId: 'Hardware ID',
    installedOn: 'Installed on',
    installedOnHelp: 'The day the box went into the cabinet.',
    simIccid: 'SIM ICCID',
    simIccidHelp:
      'Optional. 19 or 20 digits, the last one a check digit. It is what attributes the data usage.',
    requiredField: 'Required field.',
    noSim: 'No SIM',
    // The record
    record: 'Kit details',
    recordDescription: 'The kit in the cabinet: the gateway, the router and the cameras.',
    recordLoading: 'Loading the kit…',
    gatewayNotFound: 'This kit does not exist.',
    backToFleet: 'Back to the kits',
    pitch: 'Pitch',
    appVersion: 'App version',
    software: 'Software',
    targetVersion: 'Requested version',
    noTargetVersion: 'None requested',
    latestVersion: 'Newest published',
    noLatestVersion: 'Nothing published yet',
    latestVersionError: 'The published versions could not be read.',
    upgradeTo: 'Upgrade to {version}',
    upgradeCancel: 'Withdraw the request',
    upgradeAsked: 'Upgrade requested.',
    upgradeCancelled: 'Request withdrawn.',
    upgradeAll: 'Upgrade all to {version}',
    upgradeAllTitle: 'Upgrade the kits behind to {version}? Kits: {count}.',
    upgradeAllBody:
      'Each kit upgrades within a quarter of an hour, when it is not recording and has no match due. The request is withdrawn from each kit’s card.',
    upgradeAllUntried: 'Unlocks once a live kit runs {version}: upgrade one from its card first.',
    upgradeAllConfirm: 'Upgrade all',
    upgradeAllCancel: 'Not now',
    upgradeAllAsked: 'Upgrade requested. Kits asked: {count}.',
    upgradeAllNone: 'Every kit behind had already been asked.',
    maintenanceAsked: 'Request sent: the outcome shows up here within ten minutes.',
    maintenance: 'Maintenance',
    maintenanceDescription:
      'Ask the kit to redo a piece of its set-up. The kit checks in every five minutes: it receives the request on the next check-in and reports how it went on the one after, so the outcome shows up here within ten minutes.',
    maintenanceLoading: 'Loading the requests…',
    maintenanceEmpty: 'No request so far.',
    maintenanceError: 'The requests could not be loaded.',
    maintenanceConfirm: 'Ask the kit',
    maintenanceCancel: 'Cancel',
    maintenanceRequestedBy: 'Asked by',
    maintenanceNobody: '—',
    maintenanceFinishedAt: 'Finished on',
    maintenanceWaiting: 'The outcome shows up here within ten minutes of the request.',
    // Keyed by the contract's actions: a sixth one there leaves this short, and tsc says so.
    interventions: 'Interventions',
    interventionsDescription:
      'Every part swapped and the kit’s retirement, with who recorded them and what became of the part taken out.',
    interventionsLoading: 'Loading interventions…',
    interventionsEmpty: 'No intervention so far.',
    interventionsError: 'The interventions could not be loaded.',
    interventionOpenedBy: 'Recorded by',
    interventionItemOut: '{outgoing} · {fate}',
    interventionItemSwap: '{outgoing} → {incoming} · {fate}',
    interventionCancel: 'Cancel the intervention',
    interventionCancelled: 'Intervention cancelled.',
    interventionNote: 'Note',
    interventionNoteHelp: 'Optional: what happened, who was at the pitch.',
    chooseOutcome: 'Choose what becomes of it',
    simSwap: 'Swap the SIM',
    simSwapDescription:
      'Record it first, then swap the SIM in the router. The new ICCID holds at once, and the intervention closes when the kit connects with the new SIM.',
    simSwapIccid: 'ICCID of the new SIM',
    simSwapOutcome: 'What becomes of the SIM taken out',
    interveneSave: 'Record',
    interveneSaving: 'Recording…',
    intervened: 'Intervention recorded.',
    retire: 'Retire the kit',
    retireDescription:
      'The kit and its cameras leave service today, and its scheduled recordings are deleted. A retirement cannot be undone.',
    retireOutcomeOf: 'What becomes of: {part}',
    retireCamera: 'camera “{name}”',
    retireChecklist: 'Before confirming:',
    retireTailscale: 'I deleted the kit’s node from the Tailscale console.',
    retireSim: 'I asked the operator to deactivate the SIM, or the kit has none.',
    retireConfirm: 'Retire',
    retiring: 'Retiring…',
    retiredToast: 'Kit retired.',
    interventionKinds: {
      replacement: 'Replacement',
      retirement: 'Retirement',
    },
    boardSwap: 'Swap the gateway',
    boardSwapDescription:
      'Record it first, then unplug the gateway. The new board, with a freshly written SD, is switched on in the cabinet and becomes this kit’s board from the Banco. Delete the old node from the Tailscale console before switching it on.',
    boardSwapSdOnly: 'Only the SD changes: the board stays.',
    boardSwapOutcome: 'What becomes of the part taken out',
    interventionSdOnly: 'Gateway SD',
    routerSwap: 'Swap the router',
    routerSwapDescription:
      'Record it first, then unplug the router: the kit must be talking to receive the request. Once the new router is plugged in, the kit gives it the password, the APN and the camera’s reservation by itself, even without internet.',
    cameraSwap: 'Swap the camera',
    cameraSwapDescription:
      'Record it first, then unplug the camera: the kit must be talking to receive the request. Once the new camera is plugged in, the kit finds it, reserves 192.168.1.10 for it and sets it up with the kit’s password.',
    cameraSwapWhich: 'Which camera goes out',
    cameraSwapChoose: 'Choose the camera',
    maintenanceConfirmations: {
      router_wifi_off: {
        title: 'Turn the router’s Wi-Fi off?',
        body: 'The kit turns off every Wi-Fi network of the router and checks they read off. The kit does not use Wi-Fi: this is for when someone turned it back on.',
      },
      camera_dhcp_reservation: {
        title: 'Redo the camera’s reservation?',
        body: 'The router reserves the address 192.168.1.10 for the kit’s camera again. Recording does not stop.',
      },
      camera_rtsp_on: {
        title: 'Turn RTSP back on on the camera?',
        body: 'The kit turns the camera’s video stream back on and checks it answers. This is for when the camera stops sending segments.',
      },
      camera_reboot: {
        title: 'Restart the camera?',
        body: 'The camera stays off for a couple of minutes. With a recording open the kit declines: try again after the match.',
      },
      camera_apply_profile: {
        title: 'Reapply the camera profile?',
        body: 'The camera goes back to colour, with no text on the image and no audio, and its weekly restart on Monday at 4. With a recording open the kit declines: try again after the match.',
      },
      read_inventory: {
        title: 'Re-read router and camera?',
        body: 'The kit re-reads model, serial, firmware and UID of router and camera on its next heartbeat, without waiting the six hours.',
      },
    } satisfies Record<AskedMaintenanceAction, MaintenanceConfirmation>,
    // Says what will not happen, because nothing happens on the box for a quarter of an
    // hour and a button that looks instant would be read as broken.
    upgradePending:
      'The box will upgrade within a quarter of an hour, unless it is recording or has a match due.',
    // Reported by the heartbeat, like `last_seen_at`: the back-office cannot claim it.
    noAppVersion: 'Never reported',
    retiredOn: 'Retired on {date}',
    // Editing an installed box: the SIM, the campo, and the retirement date.
    edit: 'Edit the details',
    save: 'Save changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    saved: 'Changes saved.',
    // What the box itself reports, on the record card.
    freeSpace: 'Free space',
    queueDepth: 'Segments waiting to upload',
    clock: 'Clock',
    signal: 'Signal',
    signalBars: '{level}/{max}',
    neverReported: 'Never reported',
    // A box with no RTC that resumed its last known time runs behind, which is expected;
    // one running ahead was given a wrong time, which is not. The word is the diagnosis.
    clockBehind: '{skew} behind',
    clockAhead: '{skew} ahead',
    // A word for the tile's figure, the sentence for the note under it.
    clockStates: {
      synced: { short: 'On time', long: 'Synced' },
      rtc_only: { short: 'Internal', long: 'On the internal clock only' },
      unknown: { short: 'Unknown', long: 'Unknown' },
      unmeasured: { short: 'Pending', long: 'Not measured yet' },
    },
    cameras: 'The cameras on this box',
    camerasLoading: 'Loading cameras…',
    camerasError: 'The cameras could not be loaded.',
    noCameras: 'No camera on this box.',
    noCamerasDescription: 'A kit with no camera records nothing.',
    cameraMac: 'MAC address',
    cameraSerial: 'Serial number',
    cameraModel: 'Model',
    cameraFirmware: 'Firmware',
    cameraUid: 'Reolink UID',
    cameraUidHelp: 'The code under the label’s QR, e.g. 95270000ABCD1234.',
    cameraQrAlt: 'QR of the camera UID',
    cameraQrHelp: 'Scan it with the Reolink app to add the camera.',
    noCameraValue: 'Not given',
    editCamera: 'Edit the camera',
    cameraSaved: 'Camera saved.',
    cameraUidTaken: 'Another camera already has this UID.',
    cameraSerialTaken: 'Another camera already has this serial number.',
    cameraMain: 'High resolution',
    cameraSub: 'Substream',
    noStream: 'No address',
    streamStuck: 'Not producing',
    streamLastSegment: 'Last segment {when}',
    streamNeverProduced: 'Never produced anything',
    streamRelaunches: '{count} relaunches since',
  },
  bench: {
    repairOf: 'What it becomes',
    repairOfHelp: 'A new kit, or the board of a kit waiting on an intervention.',
    newKit: 'A new kit',
    repairBoard: 'The new gateway of Kit {id}',
    repairSd: 'The new SD of Kit {id}',
    title: 'At the bench',
    description:
      'Boxes switched on in the last hour. Scan the router’s code and the SIM’s ICCID, then press Prepare: the box does the rest.',
    summary: '{toPrepare} to prepare · {preparing} being prepared',
    list: 'Boxes at the bench',
    boxHeading: 'Box',
    setupHeading: 'Setup',
    progressHeading: 'Progress',
    stateHeading: 'State',
    readyToPrepare: 'Ready to prepare',
    routerMissingRow: 'Router not found: check the cable',
    beforePrepare: 'Before preparing',
    allStepsDone: 'Every step done',
    stepsProgress: '{done} of {total} steps done',
    facts: 'Box {box}',
    toPrepare: 'To prepare',
    since: 'At the bench since',
    lastSeen: 'Last heard',
    software: 'Software',
    softwareNone: '—',
    softwareRetrying: 'Retrying by itself',
    router: 'Router',
    routerFound: 'Found',
    routerMissing: 'Not found',
    routerMissingHelp: 'Check the network cable between the router and the gateway.',
    steps: 'Steps',
    stepToDo: 'To do',
    waitingForBox: 'The box picks up the job within ten seconds.',
    takenElsewhere:
      'This box was prepared from the other environment (staging or production): follow it from there.',
    prepare: 'Prepare',
    cancel: 'Cancel',
    routerCode: 'Router code',
    routerCodeHelp: 'The MAC on the label under the router: scan it or type it.',
    scanRouterCode: 'Read the router’s code',
    simIccid: 'SIM ICCID',
    simIccidHelp: 'The 19 or 20 digits under the barcode on the SIM card: scan it or type it.',
    scanSimIccid: 'Read the ICCID off the card',
    requiredField: 'Required field.',
    retry: 'Retry',
    openKit: 'Open the kit',
    loadFailed: 'Could not read the bench.',
    refetchFailed:
      'The list could not be refreshed: trying again by itself, the data below is the last read.',
    hardwareIdTaken:
      'This board already has a kit: open it from the list below. Redoing an existing kit is not done from the bench yet.',
  },
  pitch: {
    title: 'Pitches',
    description: 'The playing surfaces a club plays on. Protected area: staff only.',

    // The list
    list: 'Registered pitches',
    searchLabel: 'Search by name or address',
    createdColumn: 'Added on',
    noAddress: 'No address',
    sortLabel: 'Sort by',
    sortNameAsc: 'Name (A–Z)',
    sortNameDesc: 'Name (Z–A)',
    sortCreatedDesc: 'Newest first',
    sortCreatedAsc: 'Oldest first',
    loading: 'Loading pitches…',
    listError: 'The list of pitches could not be loaded.',
    empty: 'No pitches registered yet.',
    emptyDescription: 'Add the first pitch to get started.',
    emptySearch: 'No pitch matches this search.',
    emptySearchDescription: 'Check the spelling, or clear the field to see them all.',
    clearSearch: 'Clear the search',
    paginationLabel: 'Pagination of the pitches',
    count: '{total} pitches registered',

    // The record, on create and on edit
    newPitch: 'New pitch',
    newPitchDescription:
      'One row per playing surface: campo A and campo B of the same ground are two pitches.',
    details: 'Pitch details',
    name: 'Name',
    nameHelp: 'As the operators call it, ground included: Comunale Rivoli - campo A.',
    address: 'Address',
    addressHelp: 'Where it is. Searchable, so the town tells two "Comunale" apart.',
    lndName: 'In the federation’s comunicati',
    lndNameHelp:
      'As the federation prints it, in full: SORI - COMUNALE. That is how the comunicati import recognises the pitch — without it, matches arrive with no pitch and no camera records them.',
    lndNameEmpty: 'Not bound',
    lndCode: 'Federation ground code',
    lndCodeHelp:
      'The four digits the match programmes print before the name: 0084 XXV APRILE. With it, «Check now» recognises the pitch in the agonistic comunicati.',
    lndCodeEmpty: 'No code',
    requiredField: 'Required field.',
    create: 'Create pitch',
    creating: 'Creating…',
    edit: 'Edit',
    save: 'Save changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    created: 'Pitch created.',
    openCard: 'Open the record',
    saved: 'Changes saved.',
    pitchNotFound: 'This pitch does not exist, or has been removed.',
    backToList: 'Back to the list',
    // The società: chosen when censusing, listed on the card
    club: 'Club',
    clubHelp: 'Optional. It can also be linked later, from the club’s own record.',
    clubPlaceholder: 'Search by legal name or short name',
    clubSearching: 'Searching…',
    clubNoMatch: 'No club matches this search.',
    clearClub: 'Change club',
    isPrimary: 'It is this club’s usual pitch',
    clubs: 'Clubs',
    clubsDescription: 'Who plays here. A municipal pitch serves more than one.',
    attachClubLabel: 'Attach a club to this pitch',
    clubsLoading: 'Loading the clubs…',
    clubsError: 'The clubs of this pitch could not be loaded.',
    noClubs: 'No club plays here yet.',
    noClubsDescription: 'Attach one below, or from a club’s own Pitches section.',
  },
  season: {
    title: 'Seasons',
    description:
      'The sporting seasons. They are derived from the 1 July boundary and appear on their own: what can be changed here is when one ran, if the federation moves a boundary.',
    list: 'Seasons',
    current: 'Contains today',
    provisional: 'Provisional dates',
    startsOn: 'Starts on',
    endsOn: 'Ends on',
    correct: 'Correct',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'The boundary has been corrected',
  },

  // The categories a season is fought in, the league groups that carve one up
  // geographically, and the teams entered in them. Staff only, both ways.
  league: {
    title: 'Leagues',
    description:
      'The categories a season is organized into, the league groups that carve one up geographically, and the teams entered in them. Protected area: staff only.',

    // The list
    loading: 'Loading league groups…',
    listError: 'The league groups could not be loaded.',
    noSeasonDescription:
      'The season in the address does not exist. Pick one to see and manage its league groups.',

    newGroup: 'New league group',
    groupCode: 'Group {code}',
    codeLabel: 'Code',
    categoryLabel: 'Category',
    requiredField: 'Required field.',
    edit: 'Edit',
    cancel: 'Cancel',
    created: 'League group created.',
    saved: 'Changes saved.',
    deleted: 'League group deleted.',
    deleteGroup: 'Delete the girone',
    groupNotFound: 'This league group does not exist, or has been removed.',
    backToList: 'Back to the list',

    // The atlas: categories on the left, the open one's gironi on the right
    categoriesNav: 'Categories',
    regionsLabel: 'Regions',
    regionUnavailable: 'coming',
    regions: {
      'valle-d-aosta': { short: 'VDA', name: 'Aosta Valley' },
      piemonte: { short: 'PIE', name: 'Piedmont' },
      lombardia: { short: 'LOM', name: 'Lombardy' },
      'trentino-alto-adige': { short: 'TAA', name: 'Trentino-South Tyrol' },
      veneto: { short: 'VEN', name: 'Veneto' },
      'friuli-venezia-giulia': { short: 'FVG', name: 'Friuli-Venezia Giulia' },
      liguria: { short: 'LIG', name: 'Liguria' },
      'emilia-romagna': { short: 'EMR', name: 'Emilia-Romagna' },
      toscana: { short: 'TOS', name: 'Tuscany' },
      umbria: { short: 'UMB', name: 'Umbria' },
      marche: { short: 'MAR', name: 'Marche' },
      lazio: { short: 'LAZ', name: 'Lazio' },
      abruzzo: { short: 'ABR', name: 'Abruzzo' },
      molise: { short: 'MOL', name: 'Molise' },
      campania: { short: 'CAM', name: 'Campania' },
      puglia: { short: 'PUG', name: 'Apulia' },
      basilicata: { short: 'BAS', name: 'Basilicata' },
      calabria: { short: 'CAL', name: 'Calabria' },
      sicilia: { short: 'SIC', name: 'Sicily' },
      sardegna: { short: 'SAR', name: 'Sardinia' },
    },
    searchCategories: 'Search a category',
    noMatchingCategory: 'No category matches this search.',
    noCategoriesHere: 'Add the first one from «Manage categories».',
    delegationFilter: 'Delegation',
    wholeRegion: 'All of {region}',
    noDelegation: 'Made here',
    delegationName: '{delegation} delegation',
    withWithdrawals: 'with a withdrawal',
    groupEyebrow: 'Group',
    teamsOne: '1 team',
    teamsMany: '{count} teams',
    withdrawnOne: '1 withdrawn',
    withdrawnMany: '{count} withdrawn',
    statGroupsOne: 'group',
    statGroupsMany: 'groups',
    statTeamsOne: 'team',
    statTeamsMany: 'teams',
    statWithdrawalsOne: 'withdrawal',
    statWithdrawalsMany: 'withdrawals',
    emptyCategory: 'No group in this category yet.',
    newGroupLetter: 'New group {code}',
    correctGroup: 'Correct group {code}',
    correctCode: 'Correct the code',
    codeInputLabel: 'Group code',
    createLetter: 'Create group {code}',
    saveLetter: 'Save {code}',
    codeTaken: 'Group {code} already exists here.',

    // The categories a league group is built on
    categoriesTitle: 'Categories',
    categoriesDescription:
      'The age band, age limit, tier and gender a league group can be built on. Once a group uses one, the four lock.',
    manageCategories: 'Manage categories',
    manageSeasons: 'Manage seasons',
    nameLabel: 'Name',
    ageGroupLabel: 'Age band',
    underAgeLabel: 'Age limit',
    tierLabel: 'Tier',
    genderLabel: 'Gender',
    lockedFields: 'These fields lock once a league group uses this category.',
    categoriesLoading: 'Loading categories…',
    categoriesListError: 'The categories could not be loaded.',
    noCategories: 'No category registered yet.',
    noCategoriesDescription: 'Add the first one below.',
    newCategory: 'Add a category',
    newCategoryDescription:
      'Pick the four fields it is built on, then write the name as the bulletin will print it.',
    addCategory: 'Add',
    addingCategory: 'Adding…',
    categoryCreated: 'Category added.',
    saveCategory: 'Save',
    savingCategory: 'Saving…',
    categorySaved: 'Category saved.',
    deleteCategory: 'Delete',
    categoryDeleted: 'Category deleted.',

    // The record
    entries: 'Teams',
    entriesDescription: 'The teams entered in this league group.',
    noEntries: 'No team entered yet.',
    noEntriesDescription: 'Add the first one below.',
    withdraw: 'Withdraw',
    undoWithdraw: 'Undo withdrawal',
    withdrawn: 'Withdrawn',
    removeEntry: 'Remove',
    addTeam: 'Add a team',
    teamFieldLabel: 'Club',
    addTeamPlaceholder: 'Search by club name',
    addTeamSearching: 'Searching…',
    noCandidates: 'No team matches this search.',

    // The card's three sections
    lndDelegationKey: '{delegation} delegation · Group {code}',
    entriesSection: 'Entered',
    calendarSection: 'Fixtures',
    standingsSection: 'Standings',
  },

  // `/matches`: every fixture in a period, across gironi, with its video.
  matchOverview: {
    title: 'Fixtures',
    description: 'Every fixture in a period, and whether the cameras recorded it.',
    list: 'Fixtures in the period',
    loading: 'Loading the fixtures…',
    listError: 'The fixtures could not be loaded.',
    empty: 'No fixture in this period.',
    emptyDescription: 'Widen the period or take a filter off.',
    clearFilters: 'Clear the filters',
    fromLabel: 'From',
    toLabel: 'To',
    filters: 'Filters',
    removeFilter: 'Remove the {filter} filter',
    activeCountOne: '1 active filter',
    activeCountMany: '{count} active filters',
    pitchLabel: 'Ground',
    pitchPlaceholder: 'Search a ground',
    pitchSearching: 'Searching the grounds…',
    noPitchMatches: 'No ground by that name.',
    clubLabel: 'Club',
    clubPlaceholder: 'Search a club',
    clubSearching: 'Searching the clubs…',
    noClubMatches: 'No club by that name.',
    statusLabel: 'Status',
    allStatuses: 'All statuses',
    recordingLabel: 'Recording',
    recordingAll: 'With and without',
    recordingWith: 'Recorded',
    recordingWithout: 'Not recorded',
    countOne: '1 fixture',
    countMany: '{count} fixtures',
    paginationLabel: 'Pages of fixtures',
    columnFixture: 'Fixture',
    columnVideo: 'Recording',
    context: '{category} · {pitch}',
    videoUnplanned: 'Not planned yet',
    videoNone: 'No recording',
    missingOne: '1 segment missing',
    missingMany: '{count} segments missing',
    pitchMatches: 'Fixtures on this ground',
    clubMatches: 'Fixtures of this club',
  },

  // The calendar of a girone. The score is absent on purpose: it comes from the video, and
  // this back-office schedules and postpones without ever writing a result.
  match: {
    loading: 'Loading the fixtures…',
    listError: 'The fixtures could not be loaded.',
    empty: 'No fixture yet.',
    emptyDescription: 'Add the first one with «+ Fixture».',
    versus: '{home} – {away}',
    whenAndWhere: '{when} · {pitch}',
    withdrewSide: '{club} withdrew',

    // The giornate rail and the giornata it shows.
    railLabel: 'Matchdays',
    current: 'now',
    onlyToFix: 'Only what needs fixing ({count})',
    previousMatchday: 'Previous matchday',
    nextMatchday: 'Next matchday',
    matchdayTitle: 'Matchday {matchday}',
    backToCurrent: 'back to matchday {matchday}',
    addToMatchday: '+ Fixture',
    fixturesOne: '1 fixture',
    fixturesMany: '{count} fixtures',
    playedCount: '{count} played',
    postponedOne: '1 postponed, to place again',
    postponedMany: '{count} postponed, to place again',
    owingRefereeOne: '1 with no referee',
    owingRefereeMany: '{count} with no referee',
    withWithdrawnOne: '1 with a withdrawn side',
    withWithdrawnMany: '{count} with a withdrawn side',
    markPlayed: 'played',
    markPostponed: 'a postponed fixture',
    markOwingReferee: 'a referee owed',
    columnFixture: 'Fixture',
    columnReferee: 'Referee',
    versusSign: '–',
    versusSpoken: 'against',
    scoreSpoken: '{home} to {away}',
    refereeAdd: '+ referee',
    refereeOf: 'Referee of {fixture}',
    refereeSaved: 'Referee saved.',
    close: 'Close',

    edit: 'Edit',
    remove: 'Delete',
    removed: 'Fixture deleted.',
    // The page header's own: «Elimina» alone would sit two inches from «Elimina il video».
    removeFixture: 'Delete the fixture',

    newMatch: 'Add a fixture',
    newMatchDescription:
      'The two sides come from the teams entered; the ground from the home club, or from the search beside it.',
    editMatch: 'Edit the fixture',
    homeLabel: 'Home',
    awayLabel: 'Away',
    sidePlaceholder: 'Pick a team',
    sidesLocked: 'The two sides cannot be corrected: delete the fixture and add it again.',
    noSelectableSides: 'No team is entered, or every entered team has withdrawn.',
    pitchLabel: 'Ground',
    pitchPlaceholder: 'Pick a ground',
    pitchNeedsHome: 'Pick the home side first.',
    pitchSearchLabel: 'Or search another ground',
    pitchSearchPlaceholder: 'Search by name or address',
    pitchSearching: 'Searching…',
    noPitchMatches: 'No ground matches this search.',
    matchdayLabel: 'Matchday',
    legLabel: 'Leg',
    legPlaceholder: 'Pick one',
    kickOffDateLabel: 'Date',
    kickOffTimeLabel: 'Time',
    kickOffZoneNote: 'Date and time are Italian time, wherever you are reading this.',
    statusLabel: 'Status',
    refereeLabel: 'Referee',
    refereePlaceholder: 'Optional',
    requiredField: 'Required field.',
    create: 'Add the fixture',
    creating: 'Adding…',
    save: 'Save changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    created: 'Fixture added.',
    saved: 'Changes saved.',
  },

  // The video of a fixture: whether it arrived, and how much of it. Read-only — nothing
  // on this screen can make a camera record.
  recording: {
    pageDescription: 'The fixture, and what the cameras at this ground sent up.',
    section: 'Recordings',
    loading: 'Loading the recordings…',
    listError: 'The recordings could not be loaded.',
    empty: 'No recording for this fixture.',
    emptyDescription: 'No gateway was installed at this ground when it was scheduled.',
    matchLoading: 'Loading the fixture…',
    matchNotFound: 'Fixture not found.',
    backToLeagues: 'Go to the league groups',
    window: 'Scheduled window',
    windowValue: '{start} – {end}',
    segments: '{uploaded} segments',
    segmentsMissing: '{uploaded} segments · at least {missing} still to come',
    streamDetail: '{size} · last {when}',
    streamNothing: 'Nothing arrived',
    playerLabel: 'Recording of the match',
    playbackLoading: 'Preparing the video…',
    playbackError: 'The video could not be prepared.',
    playbackStopped: 'The video stopped. The links to the segments expire after a few hours.',
    playbackEmpty: 'Nothing to play yet.',
    playbackMissing: '{span} of the match is still to come up from the pitch.',

    remove: 'Delete the video',
    removeTitle: 'Delete the video of this session?',
    removeBody:
      'The video and the session both go, from the database and from the store. There is no way back. The copy on the pitch gateway is left alone.',
    removeConfirm: 'Delete for good',
    removed: 'Video deleted.',
    cancel: 'Cancel',
    closeDialog: 'Close',
  },

  // Each side's convocazione, as the analysis needs it: only a published one counts.
  teamSheets: {
    section: 'Call-ups',
    loading: 'Loading the call-up…',
    loadError: 'The call-up could not be loaded.',
    callUps: '{count} called up · {numbered} with a number',
  },

  highlightSettings: {
    section: 'Highlights',
    description:
      'They apply to every match: a player without the plan watches the free clips, the others are locked.',
    loading: 'Loading the highlights settings…',
    loadError: 'The highlights settings could not be loaded.',
    freeClips: 'Free clips per match',
    lifetime: 'Days a clip lives',
    lifetimeNote:
      'Counted from the day of the match: the day after, the daily check deletes the clip.',
    required: 'Required field.',
    save: 'Save',
    saved: 'Highlights settings saved, for every match.',
    shortenTitle: 'Shorten the life of the clips?',
    shortenBody:
      'The clips of matches older than {days} days will be deleted at the next daily check, for every match, and will not come back.',
    shortenConfirm: 'Delete the older clips',
    cancel: 'Cancel',
    closeDialog: 'Close',
  },

  // Sending a fixture to UbiTrack. Pseudonymous: the body carries shirt numbers, never names.
  analysis: {
    section: 'Analysis',
    loading: 'Loading the analysis…',
    loadError: 'The state of the analysis could not be loaded.',
    description:
      'UbiTrack gets the video and both call-ups with shirt numbers only: no name and no date of birth leaves.',
    submittedAt: 'Sent {when}',
    completedAt: 'Analysed {when}',
    failureDetail: 'Reason given: {detail}',
    accepted: 'UbiTrack is analysing it.',
    format: 'Match format: {players} a side',
    send: 'Send for analysis',
    sendAgain: 'Send again',
    sent: 'Fixture sent for analysis.',
    switchTurnOn: 'Turn analysis on',
    switchTurnOff: 'Turn analysis off',
    switchUnavailable: 'Off in this environment',
    switchedOn: 'Sending for analysis is on, for every match.',
    switchedOff: 'Sending for analysis is off, for every match.',
    unchanged: 'Nothing has changed since the last send: UbiTrack already has this version.',
  },

  // The standings, read-only: nothing this screen writes can move them.
  standings: {
    loading: 'Loading the standings…',
    listError: 'The standings could not be loaded.',
    empty: 'The standings are empty.',
    emptyDescription: 'They appear once teams are entered in this league group.',
    backToEntries: 'Go to the entered teams',
    notPlayedYet: 'No fixture has been played: this is the starting order.',
    disclaimer:
      'Ordered by points, goal difference, goals for and club name. Not the FIGC classifica avulsa, and it says nothing about promotion or relegation.',
    legend:
      'P played · W won · D drawn · L lost · GF goals for · GA goals against · GD goal difference · Pts points',
    withdrawnExcluded: 'Teams that withdrew are not listed.',
    table: 'Standings',
    positionColumn: 'Pos.',
    clubColumn: 'Team',
    playedColumn: 'P',
    wonColumn: 'W',
    drawnColumn: 'D',
    lostColumn: 'L',
    goalsForColumn: 'GF',
    goalsAgainstColumn: 'GA',
    goalDifferenceColumn: 'GD',
    pointsColumn: 'Pts',
  },

  // The tesseramento: the strings both the club tab and the player card render, for the
  // reason `link` exists — it belongs to neither end on its own.
  registration: {
    season: 'Season',
    seasonsLoading: 'Loading seasons…',
    seasonsError: 'The seasons could not be loaded.',
    unknownSeason: 'Season not found',
    unknownSeasonDescription:
      'The season in the address does not exist. Pick one to see and manage its players.',
    registeredOn: 'Registered on',
    registeredOnHelp: 'The date on the form. It can be backdated.',
    active: 'On the roster',
    stateFilter: 'State',
    stateAll: 'All',
    stateReleased: 'Released',
    releasedOn: 'Released on {date}',
    release: 'Release',
    released: 'Player released.',
    restore: 'Undo release',
    restored: 'Release undone.',
  },
  // The anagrafica labels neither the register nor the card owns alone: the tesseramento
  // form and a person's card both render these, for the reason `link` exists.
  identity: {
    lastName: 'Surname',
    firstName: 'First name',
    birthDate: 'Date of birth',
    birthDateHelp: 'With the two names it identifies the person.',
    position: 'Position',
    positionPlaceholder: 'Not recorded',
    sex: 'Sex',
    sexPlaceholder: 'Not recorded',
    foot: 'Foot',
    footPlaceholder: 'Not declared',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Not declared',
    nationalitySearch: 'Search a country',
    nationalityEmpty: 'No country with this name',
    taxCode: 'Codice fiscale',
    taxCodeHelp: 'Optional. Sixteen characters, as on the health card.',
    taxCodeMissing: 'Not recorded',
    phone: 'Phone',
    phoneHelp: 'Leave empty for a minor: the contact belongs to the guardian.',
    email: 'Email',
  },
  player: {
    title: 'Players',
    description: 'The players registered with this club, season by season.',
    loading: 'Loading players…',
    listError: 'The players could not be loaded.',
    empty: 'No players registered',
    emptyDescription: 'Register the first one for this season.',
    emptySearch: 'No player matches the search',
    emptySearchDescription: 'Try another surname, or clear the search.',
    clearSearch: 'Clear the search',
    searchLabel: 'Search by surname',
    list: 'Players registered with the club',
    count: '{total} players',
    paginationLabel: 'Players pages',

    register: 'Register a player',
    registerDescription: 'Search the register first: the same person must not be entered twice.',
    findLabel: 'Already on file?',
    findPlaceholder: 'Search by surname',
    findSearching: 'Searching…',
    findNoMatch: 'Nobody found. Fill the fields in to add them.',
    change: 'Change player',
    onFile: 'Taken from the register. Their details are edited on their card.',
    openCard: 'Open the card',
    submit: 'Register for {season}',
    submitting: 'Registering…',
    registered: '{name} registered.',

    registerTitle: 'Players',
    registerDescriptionPage: 'Everyone who plays, across clubs and seasons.',
    registerList: 'The register of players',
    registerHint: 'Players are registered from a club\u2019s card.',
    // Naked age ("16") loses its meaning outside the column that named it, once the row
    // is a card and not a table.
    ageYears: '{age} years old',
    notRegistered: 'Not registered',
    requiredField: 'Required field.',
    sortLabel: 'Sort by',
    sortLastNameAsc: 'Surname (A\u2013Z)',
    sortLastNameDesc: 'Surname (Z\u2013A)',
    sortBirthDateDesc: 'Youngest first',
    sortBirthDateAsc: 'Oldest first',
  },
  // The card at `/people/$id`. Anagrafica labels live in `identity`; this is the rest of
  // the card's own copy.
  person: {
    loading: 'Loading the card…',
    listError: 'The card could not be loaded.',
    details: 'Details',
    requiredField: 'Required field.',
    edit: 'Edit',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    saved: 'Person updated.',
    positionSaveFailedAfterAnagrafica:
      'The anagrafica was saved, but the position was not. Retry from the card.',
    personNotFound: 'This person does not exist',
    backToRegister: 'Back to the register',
    registrations: 'Registrations',
    registrationsDescription: 'Every season this person has been registered, newest first.',
    noRegistrations: 'Never registered',
    editAppointment: 'Edit the incarico at {club}',
    appointmentSaved: 'Incarico updated.',
    appointments: 'Roles',
    appointmentsDescription: 'Every incarico this person holds, newest season first.',
    appointmentsTable: 'The incarichi of this person',
    noAppointments: 'No incarico',
    accountTitle: 'Account',
    noAccount: 'This person has no sign-in account.',
  },
  // The organigramma: everyone at a club who does not play. The role decides whether the
  // incarico belongs to a squad, which is why `teamPlaceholder` is a state and not a hint.
  official: {
    title: 'Officials',
    description: 'Everyone who runs this club, season by season.',
    loading: 'Loading the officials…',
    listError: 'The officials of this club could not be loaded.',
    empty: 'Nobody has been appointed yet',
    emptyDescription: 'Appoint the first one for this season.',
    clubBand: 'The club',
    noTeamBand: 'No squad yet',
    noSeasonDescription:
      'The season in the address does not exist. Pick one to see and manage its officials.',
    inCharge: 'In charge',
    endedOn: 'Ended {date}',
    endedWithSeason: 'Season over',
    accountLinkedTitle: 'This person already has a Volée account',
    accountLinked:
      'They will get an invitation to accept: the incarico starts when they accept it.',
    accountUnlinkedTitle: 'There is already a Volée account at {email}',
    accountUnlinked:
      'It is linked to no anagrafica, so the incarico alone gives no access. Invite them: accepting is what links that account to this person.',
    stateFilter: 'State',
    stateAll: 'All',
    stateEnded: 'Ended',
    unknownTeam: 'Team {id}',

    findLabel: 'Already on file?',
    findPlaceholder: 'Search by surname',
    findSearching: 'Searching…',
    findNoMatch: 'Nobody found. Fill the fields in to add them.',
    onFile: 'Taken from the anagrafica. Their details are edited on their card.',
    change: 'Change person',
    appoint: 'Appoint someone',
    appointDescription:
      'A person already on file is reused: the same name and date of birth is the same person.',
    lastName: 'Surname',
    firstName: 'First name',
    birthDate: 'Date of birth',
    birthDateHelp: 'With the two names it identifies the person.',
    role: 'Role',
    rolePlaceholder: 'Pick a role',
    team: 'Team',
    teamPlaceholder: 'Pick a team',
    teamClubWide: 'This role is club-wide.',
    teamsLoading: 'Loading the teams…',
    noTeams: 'This club fields no team yet: add one first.',
    email: 'Email',
    emailHelp: 'Where the invitation goes, if one is sent.',
    phone: 'Phone',
    invite: 'Invite them to the app',
    inviteHelp: 'Not everyone needs an account: a club doctor is a row, not a login.',
    submit: 'Appoint for {season}',
    submitting: 'Appointing…',
    submitInvite: 'Invite for {season}',
    submittingInvite: 'Sending…',
    appointed: '{name} appointed.',
    requiredField: 'Required field.',

    endedOnField: 'Ended on',
    endedOnFieldHelp: 'Empty means still in charge. It can be backdated.',
    addressLabel: 'Send the invitation to',
    send: 'Send',
    cancelInvite: 'Cancel',
    end: 'End',
    ended: 'Incarico ended.',
    reopen: 'Undo',
    reopened: 'Incarico reopened.',
    sendInvitation: 'Invite',
    invitationNeedsTeam: 'One invites onto a seat only: give this person a squad first.',
    teamInviteNote: 'One invites onto a seat only: pick the squad.',
    resend: 'Resend',
    invitationMailed: 'Invitation sent to {email}.',
    revoke: 'Revoke',
    invitationRevoked: 'Invitation revoked.',
    notEmailed: 'Invitation created, but the mail did not go out: resend it from the list.',
    pendingTitle: 'Pending invitations',
    pendingError: 'The pending invitations could not be loaded.',
    pendingState: 'Pending',
    expiredState: 'Expired',
    offerExpires: 'expires {date}',
    offerExpired: 'expired {date}',
    resent: 'Invitation resent to {email}.',
    revokeTitle: 'Revoke the invitation to {email}?',
    revokeBody: 'The link will stop working.',
    revokeCancel: 'Cancel',
  },
  // Every incarico across every club: the direction the organigramma cannot read.
  register: {
    title: 'Roles',
    description: 'Everyone who holds an incarico, across every club.',
    sections: 'Sections of the roles',
    tabRegister: 'Register',
    tabRequests: 'Requests',
    list: 'The register of incarichi',
    searchLabel: 'Search by surname or first name',
    roleFilter: 'Filter by role',
    allRoles: 'All roles',
    stateFormer: 'Gone',
    stateFormerHint: 'Ended, and holding nothing anywhere else.',
    loading: 'Loading the register…',
    listError: 'The register could not be loaded.',
    empty: 'Nobody holds an incarico yet.',
    emptyDescription: 'Incarichi are assigned from a club\u2019s card.',
    emptySearch: 'Nobody matches these filters.',
    emptySearchDescription: 'Check the spelling, or clear the filters to see them all.',
    clearFilters: 'Clear the filters',
    count: '{total} people',
    paginationLabel: 'Pagination of the register',
    sortLabel: 'Sort by',
    sortLastNameAsc: 'Surname (A–Z)',
    sortLastNameDesc: 'Surname (Z–A)',
  },
  // The sign-up queue: a club's president asks to enter, and an operator decides. Its own
  // block and not `registration`, which is the tesseramento.
  registrationRequest: {
    title: 'Registration requests',
    description:
      'Clubs that asked to join Volée. Read the document, then approve, ask for more, or refuse.',
    loading: 'Loading requests…',
    listError: 'The requests could not be loaded.',
    empty: 'Nothing to review',
    emptyDescription: 'No club has asked to join yet.',
    emptyFiltered: 'No request in this state',
    emptyFilteredDescription: 'Clear the filter to see every request.',
    clearFilter: 'Clear the filter',
    statusFilter: 'State',
    allStatuses: 'All',
    list: 'Registration requests',
    paginationLabel: 'Pagination of the requests',
    count: '{total} requests',

    detailTitle: 'Registration request',
    detailDescription: 'What the applicant sent, and the decision that is yours to make.',
    backToQueue: 'Back to the requests',
    requestLoading: 'Loading the request…',
    notFound: 'That request does not exist',
    applicant: 'Applicant',
    email: 'Email',
    phone: 'Phone',
    club: 'Club',
    status: 'State',
    submittedAt: 'Submitted',
    reviewedAt: 'Decided',
    document: 'Document',
    openDocument: 'Open the document',
    noDocument: 'No document was attached.',
    notSubmitted: 'Not recorded',

    decide: 'The decision',
    // Not decoration: the same field has two readers, and the operator has to know while
    // writing it. The spec states it as a UI requirement — docs/superpowers/specs.
    reviewerNote: 'the reason (the applicant reads this)',
    reviewerNoteHelp: 'It travels in the email and appears on the applicant’s status screen.',
    reasonRequired: 'Write the reason: the applicant reads it.',
    approve: 'Approve',
    requestIntegration: 'Ask for more',
    reject: 'Refuse',
    deciding: 'Sending…',
    approved: 'Request approved.',
    integrationRequested: 'The applicant has been asked for more.',
    rejected: 'Request refused.',
    decided: 'This request has already been decided.',
    reviewerNoteSent: 'What the applicant was told',
  },
  staff: {
    title: 'Staff',
    description: 'Who can get into the back office, and until when.',

    list: 'Volée staff',
    searchLabel: 'Search by email, surname or first name',
    roleFilter: 'Filter by role',
    allRoles: 'All roles',
    stateFilter: 'Filter by state',
    allStates: 'In service and revoked',
    sortLabel: 'Sort by',
    sortEmailAsc: 'Email (A–Z)',
    sortEmailDesc: 'Email (Z–A)',
    sortSinceDesc: 'Most recent first',
    sortSinceAsc: 'Longest serving first',
    // No name on the account nor on a person: the cell says so rather than sitting empty
    // and reading as a rendering bug. The staff member fills it in from their settings.
    noName: 'No name in the profile yet',
    // The naked dates on the row's metadata line would not tell one from the other.
    sinceLine: 'in service since {date}',
    untilLine: 'until {date}',
    inService: 'In service',
    revoked: 'Revoked',
    loading: 'Loading the staff…',
    listError: 'The list of staff could not be loaded.',
    // Two distinct empty states, as on the club list: "there is nobody" and "your search
    // found nobody" are different facts, and only the second offers to clear the filters.
    empty: 'No staff member has access yet.',
    emptyDescription: 'Nobody has been invited yet.',
    emptySearch: 'No staff member matches these filters.',
    emptySearchDescription: 'Check the spelling, or clear the filters to see everyone.',
    clearFilters: 'Clear the filters',
    paginationLabel: 'Pagination of the staff',
    count: '{total} staff members',
    // Stated on the page, not left to be inferred from the absence of buttons: half of what
    // this list cannot do is still true, and saying so is what keeps it from looking unfinished.
    grantOnlyNote: 'Revoking access is still done on the database, and every change is audited.',

    invite: 'Invite a staff member',
    inviteDescription:
      'Enter the email address and the role. The person receives a temporary password by email and chooses their own on first sign-in.',
    inviteDetails: 'The invitation',
    email: 'Email',
    emailHelp: 'The Volée work address. It is also the username for signing in.',
    role: 'Role',
    roleHelp: 'Administrator also grants the right to invite other staff.',
    rolePlaceholder: 'Choose a role',
    requiredField: 'Required field.',
    inviteSubmit: 'Send the invitation',
    inviting: 'Sending…',
    cancel: 'Cancel',
    closeDialog: 'Close',
    invited: 'Invitation sent.',

    resetSecondFactor: 'Reset two-step verification',
    resetSecondFactorTitle: 'Reset two-step verification?',
    resetSecondFactorBody:
      'They will sign in with their password alone until they switch it on again, and they are told by email that this happened.',
    resetSecondFactorConfirm: 'Reset it',
    resetDone: 'Two-step verification reset.',
    resetWasOff: 'That account had no two-step verification on.',
    resetEmailFailed: 'The person could not be reached by email — tell them yourself.',
  },
  // The platform's accounts, read-only. Screens say "plan", never "subscription".
  users: {
    title: 'Users',
    description: 'Who has an account, who has a plan, and who has bought something.',

    list: 'Accounts',
    searchLabel: 'Search by email, surname or first name',
    typeFilter: 'Account type',
    customers: 'Customers',
    staffAccounts: 'Staff',
    allTypes: 'All',
    planFilter: 'Plan',
    allPlans: 'All',
    purchasesFilter: 'Purchases',
    allPurchases: 'All',
    withPurchases: 'With purchases',
    withoutPurchases: 'Without',
    sortLabel: 'Sort by',
    sortNewest: 'Newest registration',
    sortOldest: 'Oldest registration',
    sortLastSeen: 'Last access',

    free: 'Free',
    covered: 'With plan',
    paymentOverdue: 'Payment overdue',
    staff: 'Staff',
    deactivated: 'Deactivated',
    purchaseOne: '1 purchase',
    purchaseMany: '{count} purchases',
    registeredLine: 'Registered on {date}',
    lastSeenLine: 'Last access {date}',
    // A dash, not an empty slot: an account that never came back is a fact worth showing.
    never: '—',

    loading: 'Loading the accounts…',
    listError: 'The list of accounts could not be loaded.',
    empty: 'No account yet.',
    emptyDescription: 'Accounts appear here as soon as somebody signs up.',
    emptySearch: 'No account matches these filters.',
    emptySearchDescription: 'Check the spelling, or clear the filters to see every customer.',
    clearFilters: 'Clear the filters',
    paginationLabel: 'Pagination of the accounts',
    count: '{total} accounts',

    recordLoading: 'Loading the account…',
    recordError: 'The account could not be loaded.',
    notFound: 'This account does not exist.',
    backToList: 'Back to the users',
    openInStripe: 'Open in Stripe',

    accountTitle: 'Account',
    email: 'Email',
    type: 'Type',
    customer: 'Customer',
    registered: 'Registered',
    lastSeen: 'Last access',
    deactivatedOn: 'Deactivated on',
    openPerson: 'Open the person’s card',

    paysTitle: 'Plans this account pays',
    coveredByTitle: 'Plans covering this account',
    noPlan: 'No plan',
    paidBy: 'Paid by {email}',
    renewsOn: 'Renews on {date}',
    periodEndsOn: 'Period ends on {date}',
    planCancelling: 'Cancelled, ends on {date}',

    purchasesTitle: 'Purchases',
    noPurchases: 'No purchase',
    purchasedOn: 'Bought on {date}',
    revokedOn: 'Revoked on {date}',
  },
  forbidden: {
    title: 'Access denied',
    // The common case is a real Volée user with no staff row: knowing *which* account is
    // signed in is the difference between "I am stuck" and "I am on the wrong account".
    signedInAs: 'You are signed in as {email}.',
    // Keyed by the `reason` search param, so the page states which of the three it is rather
    // than blaming the role every time. `no-role` also covers a direct visit to the URL.
    reason: {
      'no-role': 'Your profile does not have permission for this section of the back office.',
      'admin-required': 'This operation is reserved for Volée administrators.',
      'not-linked':
        'Your sign-in worked, but this account is not linked to a Volée profile yet. Ask an administrator to link it, or sign in with another account.',
      deactivated: 'This account has been deactivated, so it can no longer be used.',
      // A 403 we do not recognise. It states the refusal and stops there: naming a cause we
      // have not established is how a page sends someone chasing the wrong fix.
      refused: 'This account cannot use the back office.',
    },
  },
  notFound: {
    title: 'Page not found',
    description: 'The address you opened does not exist.',
    backToHome: 'Back to the home page',
  },
  loading: {
    pending: 'Loading…',
  },
  error: {
    title: 'Something went wrong',
    retry: 'Try again',
    backToHome: 'Back to the home page',
  },
}

/** The shape every translation has to cover, derived from the source above. */
export type UiMessages = typeof EN_UI
